
import { Shield, FileCheck, BarChart3, AlertTriangle, Lock, Scale } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const FinraAssessment = () => {
  return (
    <LandingPageTemplate
      title="FINRA Cybersecurity Assessment"
      description="Comprehensive FINRA compliance assessment to evaluate and enhance your organization's cybersecurity program."
      keywords={[
        "FINRA assessment",
        "financial services cybersecurity",
        "regulatory compliance",
        "cybersecurity evaluation",
        "broker-dealer security"
      ]}
      canonicalUrl="/cybersecurity/finra-assessment/"
      schemaType="service"
      schemaData={{
        name: "FINRA Cybersecurity Assessment Services",
        description: "Expert evaluation of your security program against FINRA requirements",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Financial Services"
        highlighted="FINRA Assessment"
        description="Expert evaluation of your cybersecurity program against FINRA requirements to ensure compliance and protect your financial services firm."
        ctaText="Schedule Assessment"
        heroPattern="dots"
      />

      <FeatureSection 
        title="Comprehensive FINRA Assessment"
        subtitle="KEY FEATURES"
        description="Our FINRA assessment provides a thorough evaluation of your cybersecurity program across all critical domains."
        features={[
          {
            title: "Regulatory Alignment",
            description: "Evaluate your controls against FINRA's cybersecurity requirements and guidelines.",
            icon: <Scale className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Gap Analysis",
            description: "Identify gaps between your current security posture and FINRA requirements.",
            icon: <AlertTriangle className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Control Assessment",
            description: "In-depth evaluation of technical and administrative security controls.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Risk Scoring",
            description: "Quantitative and qualitative risk assessment based on FINRA guidelines.",
            icon: <BarChart3 className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Documentation Review",
            description: "Comprehensive review of policies, procedures, and regulatory documentation.",
            icon: <FileCheck className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Access Management",
            description: "Assessment of identity and access management controls for financial systems.",
            icon: <Lock className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection
        title="Our Assessment Approach"
        subtitle="METHODOLOGY"
        description="A systematic approach to evaluating your security program against FINRA requirements"
        steps={[
          {
            title: "Initial Scoping",
            description: "Define assessment boundaries and identify critical assets"
          },
          {
            title: "Control Review",
            description: "Assess security controls against FINRA requirements"
          },
          {
            title: "Gap Analysis",
            description: "Identify compliance gaps and security weaknesses"
          },
          {
            title: "Risk Assessment",
            description: "Evaluate risks and prioritize remediation efforts"
          },
          {
            title: "Recommendations",
            description: "Develop detailed recommendations and compliance roadmap"
          }
        ]}
      />

      <BenefitSection
        title="Why Choose Our FINRA Assessment"
        benefits={[
          {
            title: "Regulatory Expertise",
            description: "Work with certified financial services security specialists"
          },
          {
            title: "Comprehensive Coverage",
            description: "Full evaluation across all FINRA cybersecurity domains"
          },
          {
            title: "Clear Action Plan",
            description: "Actionable recommendations prioritized by risk and compliance impact"
          },
          {
            title: "Regulatory Alignment",
            description: "Ensure compliance with FINRA cybersecurity requirements"
          },
          {
            title: "Risk Reduction",
            description: "Identify and address security vulnerabilities"
          },
          {
            title: "Future Readiness",
            description: "Prepare for evolving regulatory requirements"
          }
        ]}
      />

      <CtaSection 
        title="Ready to Assess Your FINRA Compliance?"
        description="Schedule your FINRA cybersecurity assessment today and ensure your firm's regulatory compliance."
        ctaText="Schedule a Consultation"
        background="purple"
        modalTitle="Request FINRA Assessment"
        modalDescription="Tell us about your organization's needs for FINRA compliance assessment."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "FINRA Assessment",
          service_interest: "Security Assessment"
        }}
      />
    </LandingPageTemplate>
  );
};

export default FinraAssessment;
